import type { PaletteRoleType } from './siteBriefPalette';

// The card's own labels, by the primary subtag of the conversation language. The options and the question
// come from the model already written in that language.
export type ChoiceCardChromeType = {
  chooseOne: string;
  chooseAny: string;
  // Accessible names of the own-answer control and of its text field.
  customOption: string;
  customLabel: string;
  customPlaceholder: string;
  send: string;
  skip: string;
  skipped: string;
  // The question panel above the message box.
  panel: string;
  hideQuestions: string;
  showQuestions: string;
  continueQuestions: string;
  caveatInfo: string;
  ownPalette: string;
  ownPaletteHint: string;
  ownFonts: string;
  ownFontsHint: string;
  groupBrand: string;
  groupAccents: string;
  groupBase: string;
  roles: Record<PaletteRoleType, string>;
  headingFont: string;
  bodyFont: string;
  contrastLow: string;
  catalogLoading: string;
  catalogFailed: string;
  specimenHeading: string;
  specimenBody: string;
  // The sentence sent for the person when they send their own palette or fonts; it names every role and value so
  // that requirement extraction reads it like typed input.
  paletteAnswer: (colors: string[]) => string;
  fontsAnswer: (heading: string, body: string) => string;
};

const DEFAULT_CHROME_LANGUAGE = 'en';

const CHOICE_CARD_CHROME: Record<string, ChoiceCardChromeType> = {
  en: {
    chooseOne: 'Choose one',
    chooseAny: 'Choose any that apply',
    customOption: 'Write your own answer',
    customLabel: 'Your own answer',
    customPlaceholder: 'Write your own…',
    send: 'Send',
    skip: 'Skip question',
    skipped: 'Skipped',
    panel: 'Question from Evo',
    hideQuestions: 'Hide questions',
    showQuestions: 'Show questions',
    continueQuestions: 'Continue',
    caveatInfo: 'More about this option',
    ownPalette: 'Build your own palette',
    ownPaletteHint: 'Pick the five colours of the site yourself.',
    ownFonts: 'Pick your own font pair',
    ownFontsHint: 'Choose the fonts for headings and text.',
    groupBrand: 'Brand color',
    groupAccents: 'Accent colors',
    groupBase: 'Background & text',
    roles: { lightBase: 'Light base', lightAccent: 'Light accent', brand: 'Brand', darkAccent: 'Dark accent', darkBase: 'Dark base' },
    headingFont: 'Heading font',
    bodyFont: 'Body font',
    contrastLow: 'The light and dark bases are too close; text may be hard to read.',
    catalogLoading: 'Loading fonts…',
    catalogFailed: 'The fonts could not be loaded. Pick one of the options or write your own.',
    specimenHeading: 'Your site headline',
    specimenBody: 'This is how running text will look: light and easy to read.',
    paletteAnswer: ([lightBase, lightAccent, brand, darkAccent, darkBase]) =>
      `I'm composing my own palette: light base ${lightBase}, light accent ${lightAccent}, brand ${brand}, dark accent ${darkAccent}, dark base ${darkBase}.`,
    fontsAnswer: (heading, body) => `I choose the fonts: headings ${heading}, body text ${body}.`,
  },
  uk: {
    chooseOne: 'Оберіть один варіант',
    chooseAny: 'Оберіть усе, що підходить',
    customOption: 'Написати свою відповідь',
    customLabel: 'Ваша відповідь',
    customPlaceholder: 'Напишіть свій варіант…',
    send: 'Надіслати',
    skip: 'Пропустити питання',
    skipped: 'Пропущено',
    panel: 'Питання від Evo',
    hideQuestions: 'Згорнути питання',
    showQuestions: 'Показати питання',
    continueQuestions: 'Продовжити',
    caveatInfo: 'Детальніше про цей варіант',
    ownPalette: 'Складіть власну палітру',
    ownPaletteHint: 'Оберіть п’ять кольорів сайту самі.',
    ownFonts: 'Оберіть власну пару шрифтів',
    ownFontsHint: 'Виберіть шрифти для заголовків і тексту.',
    groupBrand: 'Колір бренду',
    groupAccents: 'Акцентні кольори',
    groupBase: 'Фон і текст',
    roles: { lightBase: 'Світла основа', lightAccent: 'Світлий акцент', brand: 'Бренд', darkAccent: 'Темний акцент', darkBase: 'Темна основа' },
    headingFont: 'Шрифт заголовків',
    bodyFont: 'Шрифт тексту',
    contrastLow: 'Світла й темна основи надто схожі, текст може погано читатися.',
    catalogLoading: 'Завантажуємо шрифти…',
    catalogFailed: 'Не вдалося завантажити шрифти. Оберіть один із запропонованих варіантів або напишіть свій.',
    specimenHeading: 'Заголовок вашого сайту',
    specimenBody: 'Так виглядатиме основний текст: легкий і зрозумілий для читання.',
    paletteAnswer: ([lightBase, lightAccent, brand, darkAccent, darkBase]) =>
      `Я складаю власну палітру: світла основа ${lightBase}, світлий акцент ${lightAccent}, бренд ${brand}, темний акцент ${darkAccent}, темна основа ${darkBase}.`,
    fontsAnswer: (heading, body) => `Я обираю шрифти: заголовки ${heading}, основний текст ${body}.`,
  },
  ru: {
    chooseOne: 'Выберите один вариант',
    chooseAny: 'Выберите всё, что подходит',
    customOption: 'Написать свой ответ',
    customLabel: 'Ваш ответ',
    customPlaceholder: 'Напишите свой вариант…',
    send: 'Отправить',
    skip: 'Пропустить вопрос',
    skipped: 'Пропущено',
    panel: 'Вопрос от Evo',
    hideQuestions: 'Свернуть вопросы',
    showQuestions: 'Показать вопросы',
    continueQuestions: 'Продолжить',
    caveatInfo: 'Подробнее об этом варианте',
    ownPalette: 'Составьте свою палитру',
    ownPaletteHint: 'Выберите пять цветов сайта сами.',
    ownFonts: 'Выберите свою пару шрифтов',
    ownFontsHint: 'Выберите шрифты для заголовков и текста.',
    groupBrand: 'Цвет бренда',
    groupAccents: 'Акцентные цвета',
    groupBase: 'Фон и текст',
    roles: { lightBase: 'Светлая основа', lightAccent: 'Светлый акцент', brand: 'Бренд', darkAccent: 'Тёмный акцент', darkBase: 'Тёмная основа' },
    headingFont: 'Шрифт заголовков',
    bodyFont: 'Шрифт текста',
    contrastLow: 'Светлая и тёмная основы слишком похожи, текст может плохо читаться.',
    catalogLoading: 'Загружаем шрифты…',
    catalogFailed: 'Не удалось загрузить шрифты. Выберите один из предложенных вариантов или напишите свой.',
    specimenHeading: 'Заголовок вашего сайта',
    specimenBody: 'Так будет выглядеть основной текст: лёгкий и понятный для чтения.',
    paletteAnswer: ([lightBase, lightAccent, brand, darkAccent, darkBase]) =>
      `Я составляю свою палитру: светлая основа ${lightBase}, светлый акцент ${lightAccent}, бренд ${brand}, тёмный акцент ${darkAccent}, тёмная основа ${darkBase}.`,
    fontsAnswer: (heading, body) => `Я выбираю шрифты: заголовки ${heading}, основной текст ${body}.`,
  },
  es: {
    chooseOne: 'Elige una opción',
    chooseAny: 'Elige todas las que apliquen',
    customOption: 'Escribir tu propia respuesta',
    customLabel: 'Tu respuesta',
    customPlaceholder: 'Escribe la tuya…',
    send: 'Enviar',
    skip: 'Omitir pregunta',
    skipped: 'Omitida',
    panel: 'Pregunta de Evo',
    hideQuestions: 'Ocultar preguntas',
    showQuestions: 'Mostrar preguntas',
    continueQuestions: 'Continuar',
    caveatInfo: 'Más sobre esta opción',
    ownPalette: 'Crea tu propia paleta',
    ownPaletteHint: 'Elige tú mismo los cinco colores del sitio.',
    ownFonts: 'Elige tu propia pareja de fuentes',
    ownFontsHint: 'Elige las fuentes de los títulos y del texto.',
    groupBrand: 'Color de marca',
    groupAccents: 'Colores de acento',
    groupBase: 'Fondo y texto',
    roles: { lightBase: 'Base clara', lightAccent: 'Acento claro', brand: 'Marca', darkAccent: 'Acento oscuro', darkBase: 'Base oscura' },
    headingFont: 'Fuente de los títulos',
    bodyFont: 'Fuente del texto',
    contrastLow: 'La base clara y la oscura son demasiado parecidas; el texto puede leerse mal.',
    catalogLoading: 'Cargando fuentes…',
    catalogFailed: 'No se pudieron cargar las fuentes. Elige una de las opciones o escribe la tuya.',
    specimenHeading: 'El título de tu sitio',
    specimenBody: 'Así se verá el texto principal: ligero y fácil de leer.',
    paletteAnswer: ([lightBase, lightAccent, brand, darkAccent, darkBase]) =>
      `Creo mi propia paleta: base clara ${lightBase}, acento claro ${lightAccent}, marca ${brand}, acento oscuro ${darkAccent}, base oscura ${darkBase}.`,
    fontsAnswer: (heading, body) => `Elijo las fuentes: títulos ${heading}, texto principal ${body}.`,
  },
};

// The session language uses the app's own codes ("ua"), the model answers with ISO tags ("uk", "uk-UA").
const CHROME_LANGUAGE_ALIASES: Record<string, string> = { ua: 'uk' };

export const resolveChrome = (...languages: Array<string | undefined>): ChoiceCardChromeType => {
  for (const language of languages) {
    const primary = language?.trim().toLowerCase().split(/[-_]/)[0];
    if (!primary) {
      continue;
    }
    const chrome = CHOICE_CARD_CHROME[CHROME_LANGUAGE_ALIASES[primary] ?? primary];
    if (chrome) {
      return chrome;
    }
  }
  return CHOICE_CARD_CHROME[DEFAULT_CHROME_LANGUAGE];
};
